import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';

const Logo = () => (
    <NavLink to="/" className="flex items-center -mt-2">
        <div className="w-28 h-28 rounded-full bg-white shadow-xl flex items-center justify-center border border-gray-100 overflow-hidden hover:shadow-2xl transition-shadow duration-300">
            <img
                src="/SmartAcharyaLogo-new.png"
                alt="Smart Acharya"
                className="w-full h-full object-contain transform scale-[1.75] p-2"
            />
        </div>
    </NavLink>
);

const Header = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    const toggleMenu = () => {
        if (isOpen) {
            setMobileSubmenuOpen(false);
        }
        setIsOpen(!isOpen);
    };

    const closeMenu = () => {
        setIsOpen(false);
        setMobileSubmenuOpen(false);
    };

    const handleSignIn = () => {
        closeMenu();
        navigate('/registration-form');
    };

    const whatWeOfferItems = [
        { name: 'Students & Parents', id: 'students' },
        { name: 'Tutors', id: 'teachers' },
        { name: 'Schools & Tutoring Centres', id: 'schools' },
        { name: 'Corporates & Organisations', id: 'corporates' },
    ];

    const handleDropdownItemClick = (id) => {
        setDropdownOpen(false);
        closeMenu();
        navigate(`/what-we-offer#${id}`);
    };

    const toggleMobileSubmenu = () => {
        setMobileSubmenuOpen(!mobileSubmenuOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Helper for NavLink active class – same font weight, only colour changes
    const navLinkClass = ({ isActive }) =>
        isActive
            ? 'text-[#b87333] px-3 py-2 text-sm lg:text-base font-medium'
            : 'text-[#d18b4e] hover:text-[#d18b4e]/80 px-3 py-2 text-sm lg:text-base font-medium';

    return (
        <header className="bg-white sticky top-0 z-50 border-b-0">
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 border-none">
                <div className="flex justify-between items-center">
                    {/* Logo */}
                    <div className="flex-shrink-0">
                        <Logo />
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-1 lg:space-x-2">
                        <NavLink to="/" className={navLinkClass} end>Home</NavLink>
                        <NavLink to="/about-us" className={navLinkClass}>About Us</NavLink>
                        <NavLink to="/who-we-serve" className={navLinkClass}>Who we Serve</NavLink>

                        {/* Dropdown for What we Offer */}
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                className={`px-3 py-2 text-sm lg:text-base font-medium inline-flex items-center ${location.pathname === '/what-we-offer'
                                    ? 'text-[#b87333]'
                                    : 'text-[#d18b4e] hover:text-[#d18b4e]/80'
                                    }`}
                            >
                                What we Offer
                                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {dropdownOpen && (
                                <div className="absolute left-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                                    {whatWeOfferItems.map((item) => (
                                        <button
                                            key={item.id}
                                            onClick={() => handleDropdownItemClick(item.id)}
                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#d18b4e]"
                                        >
                                            {item.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <NavLink to="/subjects-and-disciplines" className={navLinkClass}>Subjects & Disciplines</NavLink>
                    </div>

                    <div className="flex items-center space-x-4">
                        <button onClick={handleSignIn} className="text-green-800 hidden md:block border-2 border-gray-800 text-gray-800 px-4 py-2 rounded-lg font-medium hover:bg-gray-800 hover:text-white transition">
                            Sign In
                        </button>
                        {/* Mobile menu button */}
                        <button onClick={toggleMenu} className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 focus:outline-none">
                            <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Mobile Navigation */}
                {isOpen && (
                    <div className="md:hidden mt-4 space-y-2">
                        <NavLink to="/" onClick={closeMenu} className={({ isActive }) =>
                            isActive ? 'block text-[#b87333] px-3 py-2 text-sm font-medium' : 'block text-[#d18b4e] hover:text-[#d18b4e]/80 px-3 py-2 text-sm font-medium'
                        } end>Home</NavLink>

                        <NavLink to="/about-us" onClick={closeMenu} className={({ isActive }) =>
                            isActive ? 'block text-[#b87333] px-3 py-2 text-sm font-medium' : 'block text-[#d18b4e] hover:text-[#d18b4e]/80 px-3 py-2 text-sm font-medium'
                        }>About Us</NavLink>

                        <NavLink to="/who-we-serve" onClick={closeMenu} className={({ isActive }) =>
                            isActive ? 'block text-[#b87333] px-3 py-2 text-sm font-medium' : 'block text-[#d18b4e] hover:text-[#d18b4e]/80 px-3 py-2 text-sm font-medium'
                        }>Who we Serve</NavLink>

                        {/* Mobile collapsible submenu for What we Offer */}
                        <div>
                            <button
                                onClick={toggleMobileSubmenu}
                                className={`flex justify-between items-center w-full text-left px-3 py-2 text-sm font-medium ${location.pathname === '/what-we-offer'
                                    ? 'text-[#b87333]'
                                    : 'text-[#d18b4e] hover:text-[#d18b4e]/80'
                                    }`}
                            >
                                What we Offer
                                <svg className={`w-4 h-4 transition-transform ${mobileSubmenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {mobileSubmenuOpen && (
                                <div className="pl-4 border-l-2 border-gray-200 space-y-1 mt-1">
                                    {whatWeOfferItems.map((item) => (
                                        <button
                                            key={item.id}
                                            onClick={() => handleDropdownItemClick(item.id)}
                                            className="block w-full text-left text-[#d18b4e] hover:text-[#d18b4e]/80 px-3 py-2 text-sm font-medium"
                                        >
                                            {item.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <NavLink to="/subjects-and-disciplines" onClick={closeMenu} className={({ isActive }) =>
                            isActive ? 'block text-[#b87333] px-3 py-2 text-sm font-medium' : 'block text-[#d18b4e] hover:text-[#d18b4e]/80 px-3 py-2 text-sm font-medium'
                        }>Subjects & Disciplines</NavLink>

                        <button onClick={handleSignIn} className="text-green-800 w-full border-2 border-gray-800 text-gray-800 px-4 py-2 rounded-lg font-medium hover:bg-gray-800 hover:text-white transition mt-2">
                            Sign In
                        </button>
                    </div>
                )}
            </nav>
        </header>
    );
};

export default Header;